import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LuChevronDown,
  LuKeyRound,
  LuLogOut,
  LuScrollText,
  LuSettings,
  LuUser,
} from "react-icons/lu";
import { SITELINKS } from "../../app-routes/links";
import useDismiss, { DismissReason } from "../../hooks/useDismiss";
import Avatar from "../ui/Avatar";
import { handleMenuKeys } from "./menuKeys";

type ProfileMenuProps = {
  name?: string | null;
  email?: string | null;
  role?: string | null;
  photo?: string | null;
  onLogout: () => void;
};

const MENU_ITEMS = [
  { to: SITELINKS.ACCOUNT, label: "My account", icon: LuUser },
  { to: SITELINKS.CHANGE_PASSWORD, label: "Change password", icon: LuKeyRound },
  { to: SITELINKS.SETTINGS, label: "Settings", icon: LuSettings },
  { to: SITELINKS.AUDIT_LOG, label: "Audit log", icon: LuScrollText },
];

const itemClass =
  "flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors focus:outline-none";

const ProfileMenu = ({ name, email, role, photo, onLogout }: ProfileMenuProps) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const displayName = name?.trim() || "Admin";
  const firstName = displayName.split(/\s+/)[0];

  // Close when the route changes (e.g. after picking a menu item)
  useEffect(() => setOpen(false), [location.pathname]);

  // Move focus into the menu when it opens, for keyboard users
  useEffect(() => {
    if (open) {
      menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    }
  }, [open]);

  const dismiss = useCallback((reason: DismissReason) => {
    setOpen(false);
    if (reason === "escape") buttonRef.current?.focus();
  }, []);
  useDismiss(wrapRef, open, dismiss);

  return (
    <div ref={wrapRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls="profile-menu"
        className={`flex items-center gap-2.5 rounded-full py-1 pl-1 pr-2 transition-colors hover:bg-hcBg focus:outline-none focus-visible:ring-2 focus-visible:ring-honeConnectPurple/40 sm:pr-3 ${
          open ? "bg-hcBg" : ""
        }`}
      >
        <Avatar name={displayName} src={photo} size={38} />
        <span className="hidden text-left leading-tight sm:block">
          <span className="block text-sm font-semibold text-honeConnectBlack">
            Hello, {firstName}
          </span>
          {role && (
            <span className="block text-xs capitalize text-hcMuted">
              {role.replace(/_/g, " ").toLowerCase()}
            </span>
          )}
        </span>
        <LuChevronDown
          aria-hidden
          className={`h-4 w-4 text-hcMuted transition-transform duration-150 ${
            open ? "rotate-180" : ""
          }`}
        />
        <span className="sr-only">Open account menu</span>
      </button>

      {open && (
        <div
          ref={menuRef}
          id="profile-menu"
          role="menu"
          aria-label="Account"
          onKeyDown={handleMenuKeys}
          className="absolute right-0 z-30 mt-2 w-72 origin-top-right overflow-hidden rounded-xl border border-hcBorder bg-white py-1.5 shadow-pop"
        >
          {/* who is signed in */}
          <div className="flex items-center gap-3 border-b border-hcBorder px-4 pb-3.5 pt-2.5">
            <Avatar name={displayName} src={photo} size={40} />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-honeConnectBlack">
                {displayName}
              </p>
              {email && <p className="truncate text-xs text-hcMuted">{email}</p>}
            </div>
          </div>

          <div className="py-1.5">
            {MENU_ITEMS.map(({ to, label, icon: Icon }) => {
              const active = location.pathname === to;
              return (
                <Link
                  key={to}
                  to={to}
                  role="menuitem"
                  aria-current={active ? "page" : undefined}
                  className={`${itemClass} ${
                    active
                      ? "bg-hc_F4EBFA font-medium text-honeConnectPurple"
                      : "text-honeConnectBlack hover:bg-hcBg focus:bg-hcBg"
                  }`}
                >
                  <Icon
                    aria-hidden
                    className={`h-4 w-4 ${active ? "" : "text-hcMuted"}`}
                  />
                  {label}
                </Link>
              );
            })}
          </div>

          <div className="border-t border-hcBorder pt-1.5">
            <button
              type="button"
              role="menuitem"
              onClick={onLogout}
              className={`${itemClass} text-red-600 hover:bg-red-50 focus:bg-red-50`}
            >
              <LuLogOut aria-hidden className="h-4 w-4" />
              Log out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileMenu;
